/**
 * Single source of truth for every Ascentremit customer-facing fact, fee and rate.
 * Any sentence, FAQ, list item or table row that depends on a value here MUST hide
 * itself when the value is null, false, empty string or empty array. Never show
 * placeholder text like "[NUMBER]" or "TBC" to visitors.
 *
 * The three gates:
 *   - LENDING_ENABLED              hides every instalment/loan surface when false
 *   - WALLET_PARTNER_BANK          hides every "wallet earns interest / float" line
 *   - FOUNDER_NAME                 hides the second founder-story paragraph
 */
export const BRAND = {
  GREEN: "#0A3622",
  GREEN_LIGHT: "#2E7D5B",
  MINT: "#9FD3B5",
  GOLD: "#E3A23B",
  LOGO_ALT: "Ascentremit",
  SITE_URL: "https://ascentremit.com",
};

export const SITE = {
  // ---------------------- Canonical URL ----------------------
  SITE_URL: "https://ascentremit.com",

  // ----------------------- Company -----------------------
  COMPANY_LEGAL_NAME: "M.S Ascent Global Ventures Limited",
  CAC_RC_NUMBER: "RC 954393",
  REGISTERED_ADDRESS: "Maiduguri, Borno State, Nigeria",
  FOUNDED_YEAR: 2011,
  FOUNDER_NAME: "Michael Enebi Iyede",
  FOUNDER_STORY_SENTENCE:
    "Our idea is simple: let parents pay school fees the way they earn, monthly, at a fair and clearly stated price.",
  SUPPORT_HOURS: "Mon–Fri, 8am–6pm WAT",
  SUPPORT_EMAIL: "hello@ascentremit.com",
  COMPLAINTS_EMAIL: "complaints@ascentremit.com",
  PRIVACY_EMAIL: "privacy@ascentremit.com",
  PHONE_1: "+234 806 150 9247",
  PHONE_2: "+234 806 254 7058",
  WHATSAPP: "+234 806 150 9247",
  OFFICES: [{ city: "Abuja", address: "Abuja, Nigeria" }],
  GHANA_ENTITY: null,
  GHANA_REG_NUMBER: null,

  // ----------------------- Lending -----------------------
  LENDING_ENABLED: true,
  LENDING_ENTITY: "M.S Ascent Global Ventures Limited, Trading as Ascentremit",
  FCCPC_REG_NUMBER: null,
  FUNDING_PARTNER: null,
  CREDIT_BUREAU: null,                   // Name of licensed credit bureau once confirmed
  RATE_MIN_PA: 24,                       // % per year
  RATE_MAX_PA: 36,                       // % per year
  RATE_REPRESENTATIVE_PA: 30,            // typical rate used in representative examples
  TENORS_MONTHS: [3, 6, 9],
  LOAN_MIN_NGN: null,
  LOAN_MAX_NGN: null,
  APPLICATION_FEE_PCT: 1.5,              // one-time processing fee, % of principal
  EARLY_REPAYMENT_PENALTY: false,
  LATE_FEE_PCT: 2,                       // % of the missed instalment
  LATE_FEE_CAP_NGN: null,
  LATE_FEE_GRACE_DAYS: 5,
  REPAYMENT_METHOD_TEXT: "Automatically from your linked bank account or card on the due date",
  DECISION_TIME: "48 hours",
  SOFT_CHECK_AVAILABLE: true,
  REMINDER_DAYS_BEFORE: 3,
  HARDSHIP_OPTIONS: ["move a due date", "extend your plan"],
  ELIGIBILITY_TEXT: null,
  DOCUMENTS_NEEDED_TEXT: null,

  // ----------------- Wallet & payments -------------------
  WALLET_PARTNER_BANK: null,
  WALLET_RATE_PA: null,
  SCHOOL_SETTLEMENT_TIME: "the next working day",
  REFUND_DAYS: 5,
  PLATFORM_FEE_PCT: 1.5,                 // % charged to schools on each fee collected
  DSA_COMMISSION_PCT: 1.25,              // % of Ascentremit revenue paid to agents
  AGENT_COMMISSION_TEXT: "Earn 1.25% of what Ascentremit earns from the schools and families you introduce, every month",

  // ----------------------- Team --------------------------
  // Each item: { name, role, bio, photo, linkedin }. Empty array hides the section.
  TEAM: [],
};

/** Rate-gate helper: true when the value is renderable (not null/false/""/[]). */
export function has(v) {
  if (v === null || v === undefined || v === false) return false;
  if (typeof v === "string" && v.trim() === "") return false;
  if (Array.isArray(v) && v.length === 0) return false;
  return true;
}

/** True only when the representative rate sits inside the published range. */
export function ratesValid() {
  const { RATE_MIN_PA: lo, RATE_MAX_PA: hi, RATE_REPRESENTATIVE_PA: rep } = SITE;
  const ok = [lo, hi, rep].every((v) => typeof v === "number") && lo <= rep && rep <= hi;
  if (!ok) {
    // eslint-disable-next-line no-console
    console.warn(`[site] Lending figures hidden: RATE_REPRESENTATIVE_PA (${rep}%) must sit between ${lo}% and ${hi}%.`);
  }
  return ok;
}

/** One late-fee sentence used on Pricing, calculator and FAQ. */
export function lateFeeText() {
  const cap = has(SITE.LATE_FEE_CAP_NGN) ? `, capped at ₦${Number(SITE.LATE_FEE_CAP_NGN).toLocaleString("en-NG")}` : "";
  return `${SITE.LATE_FEE_PCT}% of the missed instalment, after a ${SITE.LATE_FEE_GRACE_DAYS}-day grace period${cap}.`;
}

/** Prettify a list of hardship options into "A, B or C". */
export function listJoin(arr, lastSep = "or") {
  if (!Array.isArray(arr) || arr.length === 0) return "";
  if (arr.length === 1) return arr[0];
  return arr.slice(0, -1).join(", ") + " " + lastSep + " " + arr.slice(-1);
}
